import { useStore } from '@nanostores/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { AnimatePresence, motion, type HTMLMotionProps } from 'framer-motion';
import { forwardRef, memo, useCallback, useEffect, useMemo, useState, type KeyboardEvent, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { toast } from 'react-toastify';
import { EditorPanel } from './EditorPanel';
import { LivePreviewDropdown } from './LivePreviewDropdown';
import { Preview, type PreviewRef } from './Preview';
import { HeaderActionButtons } from '~/components/header/HeaderActionButtons.client';
import { useStripeDropdownMenuItem } from '~/components/header/StripeDropdownMenuItem';
import { useSupabaseDropdownMenuItem } from '~/components/header/SupabaseDropdownMenuItem';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { SliderTabs, type SliderTabDropdownGroup, type SliderTabDropdownItem } from '~/components/ui/SliderTabs';
import type { FileChangeCallback } from '~/components/workbench/FileContextMenu';
import { chatStore } from '~/lib/stores/chat';
import { openDevicePreviewURL, useHasDevicePreviewURL } from '~/lib/stores/device-preview';
import { FilesystemError } from '~/lib/stores/files';
import { menuStore, SettingsPage } from '~/lib/stores/menu';
import { projectStore } from '~/lib/stores/project';
import { userStore } from '~/lib/stores/user';
import { workbenchStore, type WorkbenchViewType } from '~/lib/stores/workbench';
import { isDesktopOrLarger, isLaptopOrLarger } from '~/utils/breakpoints';
import { classNames } from '~/utils/classNames';
import { debounce } from '~/utils/debounce';
import { cubicEasingFn } from '~/utils/easings';
import { useInitRef } from '~/utils/init-ref';
import { renderLogger } from '~/utils/logger';
import { removeWorkDir } from '~/utils/workDir';

interface WorkspaceProps {
  open?: boolean;
  previewRef?: RefObject<PreviewRef>;
  login?: () => true | Promise<boolean>;
}

const viewTransition = { ease: cubicEasingFn };

const setSelectedView = (view: WorkbenchViewType) => {
  workbenchStore.currentView.set(view);
};

const handleViewSelection = (selected: WorkbenchViewType | string) => {
  if (selected === 'code' || selected === 'preview') {
    setSelectedView(selected as WorkbenchViewType);
  }

  if (selected === 'database') {
    menuStore.openDialog({ type: 'settings', initialPage: SettingsPage.DatabaseSection });
  }

  /**
   * For other dropdown items, we could add specific handling here.
   * For now, they're just placeholders and don't change the view.
   */
};

const onFileSelect = (filePath: string | undefined) => {
  workbenchStore.setSelectedFile(filePath);
};

const onFileSave = () => {
  workbenchStore.saveCurrentDocument().catch(() => {
    toast.error('Failed to update file content');
  });
};

const onFileReset = () => {
  workbenchStore.resetCurrentDocument();
};

const onFileChange: FileChangeCallback = async (event) => {
  if (event.method === 'rename') {
    return workbenchStore.rename(removeWorkDir(event.oldPath), removeWorkDir(event.newPath));
  }

  if (event.method === 'cut') {
    if (event.oldPath === event.newPath) {
      return undefined;
    }

    return workbenchStore.cut(removeWorkDir(event.oldPath), removeWorkDir(event.newPath), event.type);
  }

  if (event.method === 'copy') {
    const oldPath = removeWorkDir(event.oldPath);
    const newPath = removeWorkDir(event.newPath);

    if (event.type === 'file') {
      const newName = await workbenchStore.copyFile(oldPath, newPath);

      workbenchStore.setSelectedFile(newName);

      return undefined;
    }

    return workbenchStore.copyDirectory(oldPath, newPath);
  }

  const relativePath = removeWorkDir(event.path);

  if (event.type === 'file') {
    switch (event.method) {
      case 'add': {
        try {
          await workbenchStore.writeFile(
            relativePath,
            {
              type: 'file',
              content: '',
              isBinary: false,
              isStackBlitzBinary: false,
            },
            {
              errorIfExists: true,
              errorIfIgnored: true,
            },
          );
        } catch (error) {
          if (!(error instanceof FilesystemError) || error.code !== 'EEXISTS') {
            throw error;
          }
        }

        workbenchStore.setSelectedFile(event.path);

        return undefined;
      }
      case 'remove': {
        return workbenchStore.removeFile(relativePath);
      }
    }
  }

  switch (event.method) {
    case 'add': {
      await workbenchStore.createDirectory(relativePath, { errorIfIgnored: true });
      break;
    }
    case 'remove': {
      return workbenchStore.removeDirectory(relativePath);
    }
  }

  return undefined;
};

export const Workbench = memo((props: WorkspaceProps) => {
  renderLogger.trace('Workbench');

  const { open, previewRef, login } = props;

  const showChat = useStore(chatStore.showChat);
  const selectedFile = useStore(workbenchStore.selectedFile);
  const currentDocument = useStore(workbenchStore.currentDocument);
  const unsavedFiles = useStore(workbenchStore.unsavedFiles);
  const files = useStore(workbenchStore.files);
  const selectedView = useStore(workbenchStore.currentView);
  const framework = useStore(workbenchStore.framework);
  const isExpo = framework?.name === 'expo';
  const hasDevicePreviewURL = useHasDevicePreviewURL();
  const projectPermissions = useStore(projectStore.permissions);

  const [sideBySideViewAllowed, setSideBySideViewAllowed] = useState(isLaptopOrLarger());
  const [collapsePreviewButtons, setCollapsePreviewButtons] = useState(isDesktopOrLarger());

  // preview controls state for address bar and buttons
  const featureFlags = useStore(userStore.featureFlags);
  const previews = useStore(workbenchStore.previews);
  const activePreviewIndex = useStore(workbenchStore.previewsStore.activePreviewIndex);
  const activePreview = previews[activePreviewIndex];
  const [pathname, setPathname] = useState('');
  const [isPreviewControlsDropdownOpen, setIsPreviewControlsDropdownOpen] = useState(false);

  const [previewState, setPreviewState] = useState({
    isResponsiveMode: false,
    isPreviewFullscreen: false,
    hasActivePreview: false,
    iframeUrl: undefined as string | undefined,
  });

  const [previewElement] = useState(() => {
    const element = document.createElement('div');
    element.classList.add('fixed', 'inset-0', 'z-max', 'bg-bolt-elements-background-depth-2');

    return element;
  });

  const [showOverlay, setShowOverlay] = useState(false);

  const sliderTabsOptions = useMemo(() => {
    type Option = { children: React.ReactNode; label: string; value: WorkbenchViewType };

    const options: Array<Option | null> = [
      {
        children: <span className="i-lucide:eye size-4 block" />,
        label: 'Preview',
        value: 'preview',
      },
      {
        children: <span className="i-lucide:code size-4 block" />,
        label: 'Code',
        value: 'code',
      },
      projectPermissions.canOpenProjectSettings
        ? {
            children: <span className="i-heroicons:circle-stack size-4 block" />,
            label: 'Database',
            value: 'database',
          }
        : null,
    ];

    return options.filter((opt): opt is Option => opt !== null);
  }, [projectPermissions.canOpenProjectSettings]);

  const dropdownItems = useMemo(() => {
    if (!projectPermissions.canOpenProjectSettings) {
      return [];
    }

    return [
      {
        label: (
          <div className="flex items-center gap-2">
            <span className="i-heroicons:chart-bar w-4 h-4" />
            Analytics
          </div>
        ),
        value: 'analytics',
        onClick: () => {
          menuStore.openDialog({ type: 'settings', initialPage: SettingsPage.Analytics });
        },
      },
      {
        label: (
          <div className="flex items-center gap-2">
            <span className="i-heroicons:shield-check w-4 h-4" />
            Authentication
          </div>
        ),
        value: 'authentication',
        onClick: () => {
          menuStore.openDialog({ type: 'settings', initialPage: SettingsPage.Authentication });
        },
      },
      {
        label: (
          <div className="flex items-center gap-2">
            <span className="i-ph:lightbulb w-4 h-4" />
            Knowledge
          </div>
        ),
        value: 'knowledge',
        onClick: () => {
          menuStore.openDialog({ type: 'settings', initialPage: SettingsPage.KnowledgeProject });
        },
      },
      {
        label: (
          <div className="flex items-center gap-2">
            <span className="i-bolt:edge-functions w-4 h-4" />
            Server Functions
          </div>
        ),
        value: 'server-functions',
        onClick: () => {
          menuStore.openDialog({ type: 'settings', initialPage: SettingsPage.EdgeFunctions });
        },
      },
      {
        label: (
          <div className="flex items-center gap-2">
            <span className="i-lucide:key-round w-4 h-4" />
            Secrets
          </div>
        ),
        value: 'secrets',
        onClick: () => {
          menuStore.openDialog({ type: 'settings', initialPage: SettingsPage.Secrets });
        },
      },
      {
        type: 'divider' as const,
        label: '',
        value: '',
      },
      {
        label: (
          <div className="flex items-center w-full gap-2 relative">
            <span className="i-ph:gear-six-duotone w-4 h-4" />
            <span>All project settings</span>
            <span className="i-ph:arrow-right w-4 h-4 ml-auto opacity-30" />
          </div>
        ),
        value: 'backups',
        onClick: () => {
          menuStore.openDialog({ type: 'settings', initialPage: SettingsPage.Project });
        },
      },
    ];
  }, [projectPermissions.canOpenProjectSettings]);

  const stripeItemData = useStripeDropdownMenuItem();
  const supabaseItemData = useSupabaseDropdownMenuItem();

  const dropdownGroups = useMemo(() => {
    const groups: SliderTabDropdownGroup<string>[] = [];

    const integrationItems = [stripeItemData.item, supabaseItemData.item].filter((item) =>
      Boolean(item),
    ) as SliderTabDropdownItem<string>[];

    if (integrationItems.length > 0) {
      groups.push({
        header: 'Integrations',
        items: integrationItems,
      });
    }

    return groups;
  }, [stripeItemData.item, supabaseItemData.item, projectStore.integrations]);

  useEffect(() => {
    if (previewRef?.current && activePreview) {
      setPreviewState((prevState) => ({
        ...prevState,
        hasActivePreview: !!activePreview,
        iframeUrl: activePreview.baseUrl,
      }));
    }
  }, [previewRef, selectedView, activePreview]);

  useEffect(() => {
    if (!activePreview) {
      setPathname('');
      return;
    }

    const { pathname: newPathname } = new URL(activePreview.baseUrl);
    setPathname(newPathname);
  }, [activePreview]);

  useEffect(() => {
    const unsubscribeFromFramework = workbenchStore.framework.subscribe((framework) => {
      if (framework?.name === 'expo') {
        setPreviewState((prevState) => ({
          ...prevState,
          isResponsiveMode: true,
        }));

        // unsubscribe from the framework once responsive mode is enabled
        queueMicrotask(() => {
          unsubscribeFromFramework();
        });
      }
    });

    return () => {
      unsubscribeFromFramework();
    };
  }, []);

  const handleAddressChange = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (activePreview && event.key === 'Enter') {
        let newPathname = pathname;

        // normalize the pathname
        if (/^(?:www.|https?:\/\/)/.test(pathname)) {
          newPathname = '/';
        } else if (!pathname.startsWith('/')) {
          newPathname = `/${pathname}`;
        }

        try {
          new URL(activePreview.baseUrl + newPathname.replace(/^\/+/, '/'));
          previewRef?.current?.navigateTo(newPathname);
        } catch {
          newPathname = '/';
          previewRef?.current?.navigateTo(newPathname);
        }

        setPathname(newPathname);
        event.currentTarget.blur();
      }
    },
    [activePreview, pathname],
  );

  const handleReloadPreview = useCallback(() => {
    previewRef?.current?.reloadPreview();
  }, [previewRef]);

  const handleToggleResponsiveMode = useCallback(() => {
    setPreviewState((prevState) => ({
      ...prevState,
      isResponsiveMode: !prevState.isResponsiveMode,
    }));
  }, []);

  const handleToggleFullScreen = useCallback(() => {
    setShowOverlay(true);

    requestAnimationFrame(() => {
      setPreviewState((prevState) => {
        const newValue = !prevState.isPreviewFullscreen;

        if (newValue) {
          document.body.appendChild(previewElement);
        } else {
          document.body.removeChild(previewElement);
        }

        return {
          ...prevState,
          isPreviewFullscreen: newValue,
        };
      });

      setTimeout(() => {
        setShowOverlay(false);
      }, 100);
    });
  }, [previewElement]);

  const handleOpenPreviewInSeparateTab = useCallback(() => {
    previewRef?.current?.openPreviewInSeparateTab();
  }, [previewRef]);

  const editorPanelNodeRef = useInitRef(() => {
    const div = document.createElement('div');

    /**
     * Sets `display: contents` which causes an element's children to appear as if they were direct children of the
     * element's parent, ignoring the element itself.
     */
    div.classList.add('contents');

    return div;
  });

  const previewPanelNodeRef = useInitRef(() => {
    const div = document.createElement('div');
    div.classList.add('contents');

    return div;
  });

  const editorPanelPortal = createPortal(
    <EditorPanel
      editorDocument={currentDocument}
      selectedFile={selectedFile}
      files={files}
      visible={selectedView === 'code'}
      unsavedFiles={unsavedFiles}
      onFileSelect={onFileSelect}
      onFileSave={onFileSave}
      onFileReset={onFileReset}
      onFileChange={onFileChange}
    />,
    editorPanelNodeRef.current,
  );

  const previewPanelPortal = createPortal(
    <Preview
      ref={previewRef}
      login={login}
      isResponsiveMode={previewState.isResponsiveMode}
      showOverlay={showOverlay}
    />,
    previewPanelNodeRef.current,
  );

  useEffect(() => {
    const handleWindowResize = debounce(() => {
      setSideBySideViewAllowed(isLaptopOrLarger());
      setCollapsePreviewButtons(isDesktopOrLarger());
    }, 50);

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    workbenchStore.setDocuments(files);
  }, [files]);

  const setPortal = useCallback(
    (ref: HTMLDivElement | null) => {
      if (ref) {
        ref.insertBefore(editorPanelNodeRef.current, null);
      }
    },
    [editorPanelNodeRef],
  );

  const replaceWithPortal = useCallback(
    (ref: HTMLDivElement | null) => {
      if (ref) {
        ref.replaceWith(editorPanelNodeRef.current);
      }
    },
    [editorPanelNodeRef],
  );

  const setPreviewPortal = (ref: HTMLDivElement | null) => {
    if (ref) {
      ref.insertBefore(previewPanelNodeRef.current, null);
    }
  };

  const replaceWithPreviewPortal = (ref: HTMLDivElement | null) => {
    if (ref) {
      ref.replaceWith(previewPanelNodeRef.current);
    }
  };

  const showPanels = showChat || !sideBySideViewAllowed;

  const toolBarMarkup = (
    <WorkbenchToolbar
      activePreview={activePreview}
      collapsePreviewButtons={collapsePreviewButtons}
      featureFlags={featureFlags}
      hasDevicePreviewURL={hasDevicePreviewURL}
      isExpo={isExpo}
      isPreviewControlsDropdownOpen={isPreviewControlsDropdownOpen}
      onAddressChange={handleAddressChange}
      onDevicePreviewClick={openDevicePreviewURL}
      onOpenPreviewInSeparateTab={handleOpenPreviewInSeparateTab}
      onPathnameChange={setPathname}
      onPreviewControlsDropdownOpenChange={setIsPreviewControlsDropdownOpen}
      onReloadPreview={handleReloadPreview}
      onToggleFullScreen={handleToggleFullScreen}
      onToggleResponsiveMode={handleToggleResponsiveMode}
      pathname={pathname}
      previewState={previewState}
      selectedView={selectedView}
      showPanels={showPanels}
      sliderTabsOptions={sliderTabsOptions}
      dropdownItems={dropdownItems}
      dropdownGroups={dropdownGroups}
    />
  );

  if (!open) {
    return null;
  }

  if (previewState.isPreviewFullscreen) {
    return createPortal(
      <>
        {previewPanelPortal}
        <div className="z-workbench size-full select-none flex flex-col overflow-hidden">
          {toolBarMarkup}

          <div className="relative flex-1 overflow-hidden">
            <View
              ref={setPreviewPortal}
              initial={{ x: selectedView === 'preview' ? 0 : '100%' }}
              animate={{ x: selectedView === 'preview' ? 0 : '100%' }}
            />
          </div>
        </div>
      </>,
      previewElement,
    );
  }

  return (
    <>
      {previewPanelPortal}
      {editorPanelPortal}
      {stripeItemData.dialog}
      {supabaseItemData.dialog}
      <div className="z-workbench w-[var(--workbench-width)]">
        <div
          className={classNames(
            'fixed top-0 bottom-4 select-none w-[var(--workbench-inner-width)] z-0 left-[var(--workbench-left)] flex flex-col overflow-hidden',
          )}
        >
          <div
            className={classNames(
              'pointer-events-auto',
              !showChat &&
                'relative left-[calc(var(--chat-min-width)-var(--chat-padding))] w-[calc(100%-var(--chat-min-width)+var(--chat-padding))]',
            )}
          >
            {toolBarMarkup}
          </div>

          <div className="relative flex-1 overflow-hidden border border-bolt-elements-borderColor rounded-xl">
            {showPanels ? (
              <>
                <View
                  ref={setPortal}
                  initial={{ x: selectedView === 'code' ? 0 : '100%' }}
                  animate={{ x: selectedView === 'code' ? 0 : '100%' }}
                />
                <View
                  ref={setPreviewPortal}
                  initial={{ x: selectedView === 'preview' ? 0 : '-100%' }}
                  animate={{ x: selectedView === 'preview' ? 0 : '-100%' }}
                />
              </>
            ) : (
              <PanelGroup className="flex h-full" direction="horizontal">
                <Panel className="border-r border-bolt-elements-borderColor w-full" defaultSize={50}>
                  <div ref={replaceWithPortal} />
                </Panel>
                <PanelResizeHandle />
                <Panel className="w-full" defaultSize={50}>
                  <div ref={replaceWithPreviewPortal} />
                </Panel>
              </PanelGroup>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

interface ViewProps extends HTMLMotionProps<'div'> {
  children?: JSX.Element;
}

const View = forwardRef<HTMLDivElement, ViewProps>(({ children, ...props }, ref) => {
  return (
    <motion.div ref={ref} className="absolute inset-0" transition={viewTransition} {...props}>
      {children}
    </motion.div>
  );
});

interface PreviewControlButtonsProps {
  hasActivePreview: boolean;
  hasDevicePreviewURL: string;
  iframeUrl: string | undefined;
  isExpo: boolean;
  isPreviewFullscreen: boolean;
  isResponsiveMode: boolean;
  onDevicePreviewClick: () => void;
  onOpenPreviewInSeparateTab: () => void;
  onToggleFullScreen: () => void;
  onToggleResponsiveMode: () => void;
  usePreviewTunneling?: boolean;
}

const PreviewControlButtons = ({
  hasActivePreview,
  hasDevicePreviewURL,
  iframeUrl,
  isExpo,
  isPreviewFullscreen,
  isResponsiveMode,
  onDevicePreviewClick,
  onOpenPreviewInSeparateTab,
  onToggleFullScreen,
  onToggleResponsiveMode,
  usePreviewTunneling,
}: PreviewControlButtonsProps) => (
  <>
    {usePreviewTunneling ? (
      <LivePreviewDropdown openPreviewInSeparateTab={onOpenPreviewInSeparateTab} iframeUrl={iframeUrl} />
    ) : (
      <LegacyIconButton
        title="Open preview in a separate tab"
        size="sm"
        icon="i-ph:arrow-square-out"
        disabled={!iframeUrl}
        onClick={onOpenPreviewInSeparateTab}
      />
    )}
    <LegacyIconButton
      title={isResponsiveMode ? 'Exit responsive mode' : 'Responsive mode'}
      size="md"
      icon="i-ph:devices"
      disabled={!hasActivePreview}
      className={classNames(
        isResponsiveMode
          ? 'text-bolt-elements-item-contentAccent enabled:hover:text-bolt-elements-item-contentAccent'
          : 'text-bolt-elements-textSecondary enabled:hover:text-bolt-elements-textPrimary',
      )}
      onClick={onToggleResponsiveMode}
    />
    <LegacyIconButton
      className="text-bolt-elements-textSecondary enabled:hover:text-bolt-elements-textPrimary"
      size="md"
      title={isPreviewFullscreen ? 'Exit full screen' : 'Full screen'}
      icon={isPreviewFullscreen ? 'i-ph:corners-in' : 'i-ph:corners-out'}
      onClick={onToggleFullScreen}
    />
    {isExpo && (
      <LegacyIconButton
        className="text-bolt-elements-textSecondary enabled:hover:text-bolt-elements-textPrimary"
        size="md"
        title="Device Preview"
        icon="i-ph:qr-code"
        disabled={!hasDevicePreviewURL}
        onClick={onDevicePreviewClick}
      />
    )}
  </>
);

interface WorkbenchToolbarProps {
  activePreview: any;
  collapsePreviewButtons: boolean;
  featureFlags: any;
  hasDevicePreviewURL: string;
  isExpo: boolean;
  isPreviewControlsDropdownOpen: boolean;
  onAddressChange: (event: KeyboardEvent<HTMLInputElement>) => void;
  onDevicePreviewClick: () => void;
  onOpenPreviewInSeparateTab: () => void;
  onPathnameChange: (pathname: string) => void;
  onPreviewControlsDropdownOpenChange: (open: boolean) => void;
  onReloadPreview: () => void;
  onToggleFullScreen: () => void;
  onToggleResponsiveMode: () => void;
  pathname: string;
  selectedView: WorkbenchViewType;
  showPanels: boolean;
  previewState: {
    isPreviewFullscreen: boolean;
    hasActivePreview: boolean;
    iframeUrl: string | undefined;
    isResponsiveMode: boolean;
  };
  sliderTabsOptions: { children: React.ReactNode; label: string; value: WorkbenchViewType | string }[];
  dropdownItems: SliderTabDropdownItem<WorkbenchViewType | string>[];
  dropdownGroups: SliderTabDropdownGroup<WorkbenchViewType | string>[];
}

const WorkbenchToolbar = ({
  activePreview,
  collapsePreviewButtons,
  featureFlags,
  hasDevicePreviewURL,
  isExpo,
  isPreviewControlsDropdownOpen,
  onAddressChange,
  onDevicePreviewClick,
  onOpenPreviewInSeparateTab,
  onPathnameChange,
  onPreviewControlsDropdownOpenChange,
  onReloadPreview,
  onToggleFullScreen,
  onToggleResponsiveMode,
  pathname,
  previewState,
  selectedView,
  showPanels,
  sliderTabsOptions,
  dropdownItems,
  dropdownGroups,
}: WorkbenchToolbarProps) => (
  <div
    className={classNames(
      'flex relative items-center gap-2 py-2 min-h-[var(--panel-header-height)]',
      previewState.isPreviewFullscreen ? 'pl-3' : 'pl-0',
    )}
  >
    {showPanels && !previewState.isPreviewFullscreen && (
      <SliderTabs<WorkbenchViewType | string>
        selected={selectedView}
        options={sliderTabsOptions}
        setSelected={handleViewSelection}
        withBorder
        separatorIndexes={[1]}
        dropdownItems={dropdownItems}
        dropdownGroups={dropdownGroups}
      />
    )}
    {selectedView === 'preview' && activePreview && (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute w-[calc(100%-390px)] xl:w-full xl:max-w-[400px] left-[calc(50%-40px)] -translate-x-1/2 group flex items-center gap-0 flex-grow bg-bolt-elements-preview-addressBar-background border border-bolt-elements-borderColor text-bolt-elements-preview-addressBar-text rounded-full h-8 py-0.5 pl-1 pr-2 hover:bg-bolt-elements-preview-addressBar-backgroundHover hover:focus-within:bg-bolt-elements-preview-addressBar-backgroundActive focus-within:bg-bolt-elements-preview-addressBar-backgroundActive focus-within-border-bolt-elements-borderColorActive focus-within:text-bolt-elements-preview-addressBar-textActive overflow-hidden"
        >
          <div className="flex gap-1.5 w-full pl-3 pr-3 py-1">
            <input
              className="w-full bg-transparent outline-none text-xs"
              type="text"
              value={pathname}
              onChange={(event) => {
                onPathnameChange(event.target.value);
              }}
              onKeyDown={onAddressChange}
            />
          </div>

          <LegacyIconButton
            className="text-bolt-elements-textSecondary"
            size="md"
            title="Reload preview"
            icon="i-ph:arrow-clockwise"
            onClick={onReloadPreview}
          />

          {previewState.isPreviewFullscreen || (!previewState.isPreviewFullscreen && collapsePreviewButtons) ? (
            <PreviewControlButtons
              hasActivePreview={previewState.hasActivePreview}
              hasDevicePreviewURL={hasDevicePreviewURL}
              iframeUrl={previewState.iframeUrl}
              isExpo={isExpo}
              isPreviewFullscreen={previewState.isPreviewFullscreen}
              isResponsiveMode={previewState.isResponsiveMode}
              onDevicePreviewClick={onDevicePreviewClick}
              onOpenPreviewInSeparateTab={onOpenPreviewInSeparateTab}
              onToggleResponsiveMode={onToggleResponsiveMode}
              onToggleFullScreen={onToggleFullScreen}
              usePreviewTunneling={featureFlags?.previewTunneling}
            />
          ) : (
            <DropdownMenu.Root open={isPreviewControlsDropdownOpen} onOpenChange={onPreviewControlsDropdownOpenChange}>
              <DropdownMenu.Trigger asChild>
                <LegacyIconButton
                  className="text-bolt-elements-textSecondary"
                  title="Preview controls"
                  icon="i-ph:dots-three"
                  size="md"
                />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="z-preview-controls-dropdown min-w-[220px] rounded-md bg-bolt-elements-background-depth-2 p-1 border border-bolt-elements-borderColor"
                  align="start"
                  sideOffset={6}
                >
                  <div className="flex flex-col gap-1">
                    <DropdownMenu.Item
                      className="flex items-center gap-2 px-2 py-1 text-sm text-bolt-elements-textPrimary cursor-pointer rounded-sm hover:bg-bolt-elements-item-backgroundActive focus:bg-bolt-elements-item-backgroundActive outline-none"
                      onClick={onOpenPreviewInSeparateTab}
                      disabled={!previewState.iframeUrl}
                    >
                      <span className="i-bolt:external-link w-4 h-4" />
                      Open in new tab
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      className={classNames(
                        'flex items-center gap-2 px-2 py-1 text-sm cursor-pointer rounded-sm hover:bg-bolt-elements-item-backgroundActive focus:bg-bolt-elements-item-backgroundActive outline-none',
                        previewState.isResponsiveMode
                          ? 'text-bolt-elements-item-contentAccent'
                          : 'text-bolt-elements-textPrimary',
                      )}
                      onClick={onToggleResponsiveMode}
                      disabled={!previewState.hasActivePreview}
                    >
                      <span className="i-ph:devices w-4 h-4" />
                      {previewState.isResponsiveMode ? 'Exit responsive mode' : 'Responsive mode'}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      className="flex items-center gap-2 px-2 py-1 text-sm text-bolt-elements-textPrimary cursor-pointer rounded-sm hover:bg-bolt-elements-item-backgroundActive focus:bg-bolt-elements-item-backgroundActive outline-none"
                      onClick={onToggleFullScreen}
                    >
                      <span
                        className={
                          previewState.isPreviewFullscreen ? 'i-ph:corners-in w-4 h-4' : 'i-ph:corners-out w-4 h-4'
                        }
                      />
                      {previewState.isPreviewFullscreen ? 'Exit full screen' : 'Full screen'}
                    </DropdownMenu.Item>
                    {isExpo && (
                      <DropdownMenu.Item
                        className="flex items-center gap-2 px-2 py-1 text-sm text-bolt-elements-textPrimary cursor-pointer rounded-sm hover:bg-bolt-elements-item-backgroundActive focus:bg-bolt-elements-item-backgroundActive outline-none"
                        onClick={onDevicePreviewClick}
                        disabled={!hasDevicePreviewURL}
                      >
                        <span className="i-ph:qr-code w-4 h-4" />
                        Device Preview
                      </DropdownMenu.Item>
                    )}
                  </div>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
        </motion.div>
      </AnimatePresence>
    )}
    {!previewState.isPreviewFullscreen && (
      <div className="ml-auto">
        <HeaderActionButtons />
      </div>
    )}
  </div>
);
